/**
 * supabase-presets.ts
 *
 * CRUD de presets de takes no Supabase.
 *
 * SQL para criar a tabela (rode no Supabase SQL Editor):
 *
 *   create table take_presets (
 *     id          uuid primary key default gen_random_uuid(),
 *     user_id     uuid references auth.users(id) on delete cascade,
 *     project_id  text not null,          -- pode ser o slug/id do projeto no seu sistema
 *     name        text not null,
 *     preset      jsonb not null,
 *     created_at  timestamptz default now(),
 *     updated_at  timestamptz default now()
 *   );
 *
 *   -- RLS: usuário só vê os próprios presets
 *   alter table take_presets enable row level security;
 *   create policy "owner" on take_presets
 *     using (auth.uid() = user_id)
 *     with check (auth.uid() = user_id);
 *
 *   -- Índice para busca por projeto
 *   create index on take_presets(user_id, project_id);
 */

import { supabase } from '../../../supabaseClient'   // ajuste o path para o seu cliente Supabase
import type { AnimationPreset } from './take-types'

export interface SavedPreset {
  id: string
  project_id: string
  name: string
  preset: AnimationPreset
  created_at: string
  updated_at: string
}

// ─── Salvar / atualizar preset ────────────────────────────────────────────────

/**
 * Salva um preset.
 * - Se `id` for passado, faz upsert (atualiza).
 * - Se não, cria um novo registro.
 *
 * Retorna o registro salvo ou lança erro.
 */
export async function savePreset(
  projectId: string,
  preset: AnimationPreset,
  existingId?: string,
): Promise<SavedPreset> {
  const { data: { user }, error: authError } = await supabase.auth.getUser()
  if (authError || !user) throw new Error('Usuário não autenticado')

  const payload = {
    ...(existingId ? { id: existingId } : {}),
    user_id:    user.id,
    project_id: projectId,
    name:       preset.name,
    preset:     preset,
    updated_at: new Date().toISOString(),
  }

  const { data, error } = await supabase
    .from('take_presets')
    .upsert(payload, { onConflict: 'id' })
    .select()
    .single()

  if (error) throw error
  return data as SavedPreset
}

// ─── Listar presets de um projeto ─────────────────────────────────────────────

export async function listPresets(projectId: string): Promise<SavedPreset[]> {
  const { data, error } = await supabase
    .from('take_presets')
    .select('id, project_id, name, preset, created_at, updated_at')
    .eq('project_id', projectId)
    .order('updated_at', { ascending: false })

  if (error) throw error
  return (data ?? []) as SavedPreset[]
}

// ─── Carregar um preset específico ───────────────────────────────────────────

export async function loadPreset(id: string): Promise<SavedPreset> {
  const { data, error } = await supabase
    .from('take_presets')
    .select('*')
    .eq('id', id)
    .single()

  if (error) throw error
  return data as SavedPreset
}

// ─── Deletar preset ───────────────────────────────────────────────────────────

export async function deletePreset(id: string): Promise<void> {
  const { error } = await supabase
    .from('take_presets')
    .delete()
    .eq('id', id)

  if (error) throw error
}
